import type {
  RawCompetition,
  RawMatch,
  RawMatchStatus,
  RawTeam,
} from "@/services/football/footballApiTypes";
import type { Competition } from "@/modules/football/types/football";
import type { Club } from "@/modules/leagues/types/admin";
import type { Match, MatchStatus, TeamRef } from "@/modules/leagues/types/match";

/**
 * Football API → domínio (item 4 do escopo). Único lugar do projeto que
 * conhece o formato bruto da football-data.org — todo o resto do app só
 * enxerga os tipos normalizados (Competition, Club, Match já existentes).
 */

export function mapCompetition(raw: RawCompetition): Competition {
  return {
    id: raw.code,
    name: raw.name,
    country: raw.area.name,
    emblemUrl: raw.emblem ?? null,
    currentMatchday: raw.currentSeason?.currentMatchday ?? null,
  };
}

export function mapTeamToClub(raw: RawTeam): Club {
  return {
    id: String(raw.id),
    name: raw.name,
    shortName: raw.tla || raw.shortName,
    crestUrl: raw.crest ?? null,
  };
}

function mapTeamToTeamRef(raw: RawMatch["homeTeam"]): TeamRef {
  return {
    id: String(raw.id),
    name: raw.name,
    shortName: raw.tla || raw.shortName,
    crestUrl: raw.crest ?? null,
  };
}

/**
 * Normaliza o status bruto para os 3 estados principais do domínio
 * (item 9 do escopo) — estados extras da API (adiada/cancelada/suspensa)
 * são mapeados sem quebrar os 3 estados que a UI já conhece.
 */
export function mapMatchStatus(raw: RawMatchStatus): MatchStatus {
  switch (raw) {
    case "SCHEDULED":
    case "TIMED":
      return "scheduled";
    case "IN_PLAY":
    case "PAUSED":
      return "live";
    case "FINISHED":
    case "AWARDED":
      return "finished";
    case "POSTPONED":
    case "SUSPENDED":
    case "CANCELLED":
      return "postponed";
    default:
      return "scheduled";
  }
}

/**
 * Nome amigável de cada fase — só usado quando `roundLabel` precisa
 * desambiguar (competição com fases, onde o número de rodada reinicia a
 * cada fase nova). Cobre os valores documentados da football-data.org;
 * fase não mapeada aqui cai no fallback (mostra o valor cru da API, nunca
 * quebra por causa de um nome de fase novo/desconhecido).
 */
const STAGE_LABELS: Record<string, string> = {
  GROUP_STAGE: "Fase de grupos",
  LEAGUE_STAGE: "Fase de liga",
  // "PLAY_OFFS" na Libertadores é literalmente a fase de oitavas de final
  // do mata-mata (confirmado pelo usuário, terminologia real da
  // competição) — "Playoffs" isolado não dizia nada pro usuário brasileiro.
  // Como esse rótulo é global (não por competição), isso vale pra
  // qualquer competição do football-data.org que usar essa mesma fase —
  // hoje só a Libertadores usa isso na prática.
  PLAY_OFFS: "Oitavas de final",
  ROUND_1: "1ª fase",
  ROUND_2: "2ª fase",
  ROUND_3: "3ª fase",
  ROUND_4: "4ª fase",
  LAST_16: "Oitavas",
  QUARTER_FINALS: "Quartas de final",
  SEMI_FINALS: "Semifinal",
  FINAL: "Final",
};

export function stageLabel(stage: string | null): string | null {
  if (!stage || stage === "REGULAR_SEASON") return null; // sem fase = liga simples de pontos corridos, não precisa desambiguar
  return STAGE_LABELS[stage] ?? stage;
}

export function mapMatch(raw: RawMatch, leagueId: string): Match {
  const status = mapMatchStatus(raw.status);
  const round = raw.matchday ?? 0;
  const stage = raw.stage ?? null;
  const stageName = stageLabel(stage);
  const roundLabel = round > 0 ? (stageName ? `${stageName} - Rodada ${round}` : `Rodada ${round}`) : (stageName ?? "Rodada atual");
  return {
    id: `fd-${raw.id}`,
    leagueId,
    round,
    roundLabel,
    stage,
    competitionId: raw.competition.code,
    homeTeam: mapTeamToTeamRef(raw.homeTeam),
    awayTeam: mapTeamToTeamRef(raw.awayTeam),
    kickoff: raw.utcDate,
    stadium: raw.venue ?? null,
    status,
    homeScore: raw.score.fullTime.home,
    awayScore: raw.score.fullTime.away,
    // Palpites nunca vêm da Football API — sempre sobrepostos pela camada
    // de persistência de palpites do próprio app (ver FootballDataMatchRepository).
    userPrediction: null,
    predictionsLocked: status !== "scheduled",
  };
}
